/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import { filterDOMProps } from '@react-aria/utils';
import * as stylex from '@stylexjs/stylex';
import { createContext, type ForwardedRef, forwardRef, useRef } from 'react';
import { mergeProps, useFocusRing, useHover, usePress, useSearchField } from 'react-aria';
import {
  type ContextValue,
  DEFAULT_SLOT,
  Input as InputCore,
  InputContext as InputCoreContext,
  type InputProps as InputCoreProps,
  Provider,
  SearchFieldContext as SearchFieldCoreContext,
  type SearchFieldProps as SearchFieldCoreProps,
  useContextProps,
  useRenderProps
} from 'react-aria-components';
import { useSearchFieldState } from 'react-stately';
import { CloseStrokeIcon } from '../../icons/close-stroke/close-stroke.icon';
import { FilterV2StrokeIcon } from '../../icons/filter-v-2-stroke/filter-v-2-stroke.icon';
import { MicStrokeIcon } from '../../icons/mic-stroke/mic-stroke.icon';
import { SearchRightStrokeIcon } from '../../icons/search-right-stroke/search-right-stroke.icon';
import { $ } from '../../tokens.stylex';
import type { IconButtonProps } from '../icon-button/icon-button.component';
import { IconButton, IconButtonContext } from '../icon-button/icon-button.component';
import { IconContext } from '../icon/icon.component';

interface SearchFieldProps extends Omit<SearchFieldCoreProps, 'style' | 'className'>, Pick<InputCoreProps, 'placeholder'> {
  /** The variant of the input. (Default: outlined) */
  variant?: keyof typeof variantVariants;
  /** The size variant of the input. (Default: 48) */
  size?: keyof typeof sizeVariants;
  /** Whether the input is rounded. (Default: false) */
  isRounded?: boolean;
  style?: stylex.StyleXStyles;
}

const SearchFieldContext = createContext<ContextValue<SearchFieldProps, HTMLDivElement>>({});

const SearchField = forwardRef((props: SearchFieldProps, ref: ForwardedRef<HTMLDivElement>) => {
  [props, ref] = useContextProps(props, ref, SearchFieldContext);
  [props, ref] = useContextProps(props, ref, SearchFieldCoreContext);

  const inputRef = useRef<HTMLInputElement>(null);
  const [inputContextProps, mergedInputRef] = useContextProps({}, inputRef, InputCoreContext);

  const state = useSearchFieldState(props);
  const { hoverProps, isHovered } = useHover(props);
  const { isFocusVisible, focusProps, isFocused } = useFocusRing({
    isTextInput: false,
    within: true,
    autoFocus: props.autoFocus
  });
  const { pressProps } = usePress({
    onPressStart: () => mergedInputRef.current?.focus(),
    preventFocusOnPress: true,
  });

  const { inputProps, clearButtonProps, ...validation } = useSearchField({
    'aria-label': 'Search',
    ...props,
  }, state, mergedInputRef);

  const { style, size = 48, isRounded = false, placeholder = 'Search', children, variant = 'outlined', ...coreProps } = props;

  const renderProps = useRenderProps({
    values: {
      isEmpty: state.value === '',
      isDisabled: props.isDisabled || false,
      isInvalid: validation.isInvalid || false,
      isFocused,
      isFocusVisible,
      state,
    },
    children: (renderProps) => {
      renderProps.defaultChildren = (
        <div {...stylex.props(inputContainerStyles.base)}>
          <InputCore />
          <IconButton slot={SEARCH_FIELD_SLOTS.CLEAR_ICON_BUTTON} />
        </div>
      );

      return (
        <Provider values={[
          [InputCoreContext, {
            ...mergeProps(inputProps, inputContextProps),
            ref: mergedInputRef,
            disabled: coreProps.isDisabled,
            placeholder,
            ...stylex.props(
              inputStyles.base,
              inputSizeVariants[size],
              isHovered && inputStyles.hovered,
              renderProps.isDisabled && inputStyles.disabled,
              (isFocusVisible || isFocused) && inputStyles.focused,
              variant === 'filled' && inputStyles.tertiaryPlaceholder
            )
          }],
          [IconButtonContext, {
            slots: {
              [DEFAULT_SLOT]: {
                variant: 'ghost',
                size: ICON_BUTTON_SIZES[size],
                isRounded,
              },
              [SEARCH_FIELD_SLOTS.CLEAR_ICON_BUTTON]: {
                variant: 'ghost',
                size: ICON_BUTTON_SIZES[size],
                children: <CloseStrokeIcon />,
                isRounded,
                style: renderProps.isEmpty && clearButtonStyles.hidden,
                ...clearButtonProps,
              },
              [SEARCH_FIELD_SLOTS.FILTER_ICON_BUTTON]: {
                variant: 'ghost',
                size: ICON_BUTTON_SIZES[size],
                isRounded,
              },
              [SEARCH_FIELD_SLOTS.VOICE_ICON_BUTTON]: {
                variant: 'ghost',
                size: ICON_BUTTON_SIZES[size],
                isRounded,
                children: <MicStrokeIcon />,
                excludeFromTabOrder: true,
                preventFocusOnPress: true,
                'aria-label': 'Search with voice',
              },
              [SEARCH_FIELD_SLOTS.FILTER_ICON_BUTTON]: {
                variant: 'ghost',
                size: ICON_BUTTON_SIZES[size],
                isRounded,
                children: <FilterV2StrokeIcon />,
                excludeFromTabOrder: true,
                preventFocusOnPress: true,
                'aria-label': 'Filter',
              },
            }
          }],
          [IconContext, {
            slots: {
              [DEFAULT_SLOT]: {},
              [SEARCH_FIELD_SLOTS.SEARCH_ICON]: {
                size: 20,
                style: searchIconStyles.base,
                children: <SearchRightStrokeIcon />,
              },
            }
          }]
        ]}>
          {typeof children === 'function' ? children(renderProps) : children || renderProps.defaultChildren}
        </Provider>
      );
    }
  });

  const DOMProps = filterDOMProps(props);
  delete DOMProps.id;

  return (
    <div
      ref={ref}
      slot={props.slot || undefined}
      data-empty={state.value === '' || undefined}
      data-disabled={props.isDisabled || undefined}
      data-invalid={validation.isInvalid || undefined}
      data-focused={isFocused || undefined}
      data-focus-visible={isFocusVisible || undefined}
      data-hovered={isHovered || undefined}
      {...DOMProps}
      {...renderProps}
      {...focusProps}
      {...pressProps}
      {...hoverProps}
      {...stylex.props(
        styles.base,
        sizeVariants[size],
        variantVariants[variant],
        isRounded && styles.rounded,
        state.value !== '' && styles.filled,
        style
      )}>
      {renderProps.children}
    </div>
  );
});

const ICON_BUTTON_SIZES: Record<keyof typeof sizeVariants, IconButtonProps['size']> = {
  52: 48,
  48: 40,
  44: 40,
  36: 32,
  32: 28,
  28: 24,
};

const styles = stylex.create({
  base: {
    alignItems: 'center',
    borderStyle: 'solid',
    boxSizing: 'border-box',
    cursor: {
      default: 'text',
      ':is([data-disabled])': 'not-allowed',
    },
    display: 'flex',
    overflow: 'hidden',
  },
  rounded: { borderRadius: 999 },
  filled: {
    backgroundColor: $.opacity04,
    borderColor: 'transparent',
  },
});

const inputContainerStyles = stylex.create({
  base: {
    alignItems: 'center',
    display: 'flex',
    flex: '1 1 0',
  },
});

const inputStyles = stylex.create({
  base: {
    backgroundColor: 'transparent',
    borderStyle: 'none',
    boxSizing: 'border-box',
    caretColor: $.textInteractive,
    color: $.textPrimary,
    fontFamily: 'BasierCircle',
    fontFeatureSettings: 'liga off, clig off',
    fontStyle: 'normal',
    fontWeight: 400,
    minWidth: 0,
    outline: 'none',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%',
    '::placeholder': { color: $.textPlaceholder },
    '::selection': { backgroundColor: $.surfaceHighlightMedium },
  },
  hovered: { '::placeholder': { color: $.textPrimary } },
  disabled: { '::placeholder': { color: $.textDisabled } },
  focused: { '::placeholder': { color: $.textPlaceholder } },
  tertiaryPlaceholder: { '::placeholder': { color: $.textTertiary } },
});

const variantVariants = stylex.create({
  outlined: {
    backgroundColor: {
      default: $.surfaceBGPrimary,
      ':is([data-focused], [data-focus-within], [data-hovered])': $.opacity04,
    },
    borderColor: {
      default: $.border16,
      ':is([data-focused], [data-focus-within], [data-hovered])': 'transparent',
    },
    borderWidth: 1,
  },
  filled: {
    backgroundColor: $.opacity06,
    borderWidth: 0,
  },
  underlined: {
    backgroundColor: 'transparent',
    borderBottomColor: {
      default: $.border08,
      ':is([data-hovered], [data-focused], [data-focus-within]):not([data-invalid])': $.borderInteractive,
      ':is([data-disabled])': $.border06,
      ':is([data-invalid])': $.borderCritical,
    },
    borderBottomWidth: 1.5,
    borderLeftWidth: 0,
    borderRadius: 0,
    borderRightWidth: 0,
    borderTopWidth: 0,
    paddingLeft: 0,
    paddingRight: 0,
  },
});

const sizeVariants = stylex.create({
  52: {
    borderRadius: 12,
    fontSize: 16,
    height: 52,
    letterSpacing: -0.32,
    minHeight: 52,
    paddingLeft: 16,
    paddingRight: 1,
  },
  48: {
    borderRadius: 10,
    fontSize: 16,
    height: 48,
    letterSpacing: -0.32,
    minHeight: 48,
    paddingLeft: 16,
    paddingRight: 3,
  },
  44: {
    borderRadius: 10,
    fontSize: 16,
    height: 44,
    letterSpacing: -0.32,
    minHeight: 44,
    paddingLeft: 16,
    paddingRight: 1,
  },
  36: {
    borderRadius: 8,
    fontSize: 14,
    height: 36,
    letterSpacing: -0.252,
    minHeight: 36,
    paddingLeft: 12,
    paddingRight: 1,
  },
  32: {
    borderRadius: 8,
    fontSize: 14,
    height: 32,
    letterSpacing: -0.252,
    minHeight: 32,
    paddingLeft: 10,
    paddingRight: 1,
  },
  28: {
    borderRadius: 6,
    fontSize: 12,
    height: 28,
    letterSpacing: -0.12,
    minHeight: 28,
    paddingLeft: 8,
    paddingRight: 1,
  },
});

const inputSizeVariants = stylex.create({
  52: {
    fontSize: 16,
    letterSpacing: -0.32,
  },
  48: {
    fontSize: 16,
    letterSpacing: -0.32,
  },
  44: {
    fontSize: 16,
    letterSpacing: -0.32,
  },
  36: {
    fontSize: 14,
    letterSpacing: -0.252,
  },
  32: {
    fontSize: 14,
    letterSpacing: -0.252,
  },
  28: {
    fontSize: 12,
    letterSpacing: -0.12,
  },
});

const clearButtonStyles = stylex.create({
  hidden: {
    opacity: 0,
    pointerEvents: 'none',
  },
});

const searchIconStyles = stylex.create({
  base: { marginRight: 12 },
});

const SEARCH_FIELD_SLOTS = {
  CLEAR_ICON_BUTTON: 'clear-icon-button',
  FILTER_ICON_BUTTON: 'filter-icon-button',
  VOICE_ICON_BUTTON: 'voice-icon-button',
  SEARCH_ICON: 'search-icon',
};

export { SEARCH_FIELD_SLOTS, SearchField, SearchFieldContext, type SearchFieldProps };
